/**
 * #978 — card note domain types.
 *
 * The shapes follow the proposal in `docs/260926FeatureDeliveryContracts.md`
 * §1 ("Senior persistence baseline"). They are a CLIENT contract for the
 * adapter seam, not an endpoint promise: #976 owns identity, schema,
 * authorization and the live adapter, and may reshape the wire format behind
 * `CardNoteAdapter` without touching the editor.
 */

export type CardNoteVenue = 'kalshi' | 'polymarket';

/**
 * Exactly one note-bearing thing. Never keyed by display title, array index,
 * game, synthetic row id, or another outcome's token (contract §1).
 *
 * `scope` is the resolved user/org/venue-account scope, OPAQUE to the client:
 * the integrator (#976) computes it and the server re-validates access rather
 * than trusting it. Two accounts holding the same ticker therefore produce two
 * different targets.
 */
export type CardNoteTarget =
  | {
      kind: 'order';
      scope: string;
      venue: CardNoteVenue;
      /** Immutable venue order id (Kalshi order_id / Polymarket order hash). */
      venueOrderId: string;
    }
  | {
      kind: 'position';
      scope: string;
      venue: CardNoteVenue;
      /** Exact market identity: Kalshi ticker, Polymarket condition id. */
      marketId: string;
      /** The HELD outcome, so a YES and a NO on one market are two notes. */
      outcome: 'yes' | 'no';
    };

/** A target without its scope — what a card knows about itself. */
export type CardNoteSubject =
  | Omit<Extract<CardNoteTarget, { kind: 'order' }>, 'scope'>
  | Omit<Extract<CardNoteTarget, { kind: 'position' }>, 'scope'>;

/** The canonical saved state. `note: null` and `revision: 0` = never written. */
export interface CardNoteRecord {
  target: CardNoteTarget;
  note: string | null;
  /** Server revision. A write names the revision it was based on. */
  revision: number;
  /** ISO 8601 of the last write, or null when never written. */
  updatedAt: string | null;
}

/**
 * The persistence seam. Read and save are separate operations (contract §1).
 * The production implementation is `createCardNoteAdapter` over the card-annotation
 * client (`lib/cardAnnotations/adapters.ts`); there is deliberately no localStorage fallback.
 */
export interface CardNoteAdapter {
  /**
   * Canonical records for a bounded batch of targets. A target missing from
   * the result has never been written (the controller treats it as empty).
   */
  read(targets: readonly CardNoteTarget[]): Promise<readonly CardNoteRecord[]>;
  /**
   * Save `note` (null clears) against `expectedRevision`. Resolves with the
   * canonical saved record; rejects with a `CardNoteSaveError` on conflict or
   * refusal, or with anything else on transport failure.
   */
  save(
    target: CardNoteTarget,
    note: string | null,
    expectedRevision: number,
  ): Promise<CardNoteRecord>;
}

/**
 * Typed save refusal. `conflict` carries the canonical record that won, so the
 * editor can offer reload/retry instead of overwriting silently.
 */
export class CardNoteSaveError extends Error {
  readonly reason: 'conflict' | 'forbidden';
  readonly latest: CardNoteRecord | null;

  constructor(reason: 'conflict', latest: CardNoteRecord);
  constructor(reason: 'forbidden', latest?: null);
  constructor(reason: 'conflict' | 'forbidden', latest: CardNoteRecord | null = null) {
    super(reason === 'conflict' ? 'Note changed since it was loaded' : 'Not allowed to edit this note');
    this.name = 'CardNoteSaveError';
    this.reason = reason;
    this.latest = latest;
  }
}

/** Stable string identity for a target: the store key. */
export function targetKey(t: CardNoteTarget): string {
  return t.kind === 'order'
    ? JSON.stringify(['order', t.scope, t.venue, t.venueOrderId])
    : JSON.stringify(['position', t.scope, t.venue, t.marketId, t.outcome]);
}

export function withScope(subject: CardNoteSubject, scope: string): CardNoteTarget {
  return { ...subject, scope } as CardNoteTarget;
}
